import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  Crown,
  Zap,
  Shield,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  free: {
    Icon: Shield,
    gradient: 'from-slate-500 to-slate-700',
    border: 'border-slate-300',
    badgeCls: 'bg-slate-100 text-slate-700',
    ringCls: 'ring-slate-300',
  },
  premium: {
    Icon: Zap,
    gradient: 'from-amber-500 to-orange-600',
    border: 'border-amber-300',
    badgeCls: 'bg-amber-100 text-amber-700',
    ringCls: 'ring-amber-400',
  },
  pro: {
    Icon: Crown,
    gradient: 'from-violet-600 to-purple-700',
    border: 'border-violet-300',
    badgeCls: 'bg-violet-100 text-violet-700',
    ringCls: 'ring-violet-400',
  },
};

const FEATURES = {
  free: [
    { text: 'Create 1 Ajo group', ok: true },
    { text: 'Join up to 2 groups', ok: true },
    { text: 'Max group size: 6 members', ok: true },
    { text: '2 savings goals', ok: true },
    { text: 'Basic wallet access', ok: true },
    { text: 'Lower service fees', ok: false },
    { text: 'Group analytics', ok: false },
    { text: 'Priority payout alerts', ok: false },
  ],
  premium: [
    { text: 'Create up to 5 groups', ok: true },
    { text: 'Join up to 10 groups', ok: true },
    { text: 'Max group size: 12 members', ok: true },
    { text: 'Unlimited savings goals', ok: true },
    { text: 'Lower service fees (1%)', ok: true },
    { text: 'Group reminders', ok: true },
    { text: 'Early access to features', ok: true },
    { text: 'Full group analytics', ok: false },
  ],
  pro: [
    { text: 'Unlimited group creation', ok: true },
    { text: 'Unlimited group joining', ok: true },
    { text: 'Max group size: 12 members', ok: true },
    { text: 'Unlimited savings goals', ok: true },
    { text: 'Lowest fees (0.5%)', ok: true },
    { text: 'Full group analytics', ok: true },
    { text: 'Priority payout alerts', ok: true },
    { text: 'Invite users directly', ok: true },
  ],
};

function UsageCard({ usage }) {
  const createdMax = usage.max_create === -1 ? '\u221e' : usage.max_create;
  const joinedMax = usage.max_join === -1 ? '\u221e' : usage.max_join;
  const goalsMax = usage.max_savings_goals === -1 ? '\u221e' : usage.max_savings_goals;
  const createPct = usage.max_create > 0 ? (usage.groups_created / usage.max_create) * 100 : 0;
  const joinPct = usage.max_join > 0 ? (usage.groups_joined / usage.max_join) * 100 : 0;
  const goalsPct = usage.max_savings_goals > 0 ? (usage.savings_goals_active / usage.max_savings_goals) * 100 : 0;

  return (
    <Card className="mb-6 border-2 border-slate-200" data-testid="usage-card">
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-slate-600 mb-3">Your Usage</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] text-slate-500">Groups Created</p>
            <p className="text-lg font-bold text-slate-900">{usage.groups_created}/{createdMax}</p>
            {usage.max_create > 0 && <Progress value={createPct} className="h-1.5 mt-1" />}
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Groups Joined</p>
            <p className="text-lg font-bold text-slate-900">{usage.groups_joined}/{joinedMax}</p>
            {usage.max_join > 0 && <Progress value={joinPct} className="h-1.5 mt-1" />}
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Savings Goals</p>
            <p className="text-lg font-bold text-slate-900">{usage.savings_goals_active}/{goalsMax}</p>
            {usage.max_savings_goals > 0 && <Progress value={goalsPct} className="h-1.5 mt-1" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FeatureRow({ text, ok }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <Check size={16} className="text-emerald-500 flex-shrink-0" />
      ) : (
        <X size={16} className="text-slate-300 flex-shrink-0" />
      )}
      <span className={'text-sm ' + (ok ? 'text-slate-700' : 'text-slate-400 line-through')}>{text}</span>
    </div>
  );
}

function PlanCard({ planKey, plan, isCurrent, expiry, onUpgrade, upgrading }) {
  const meta = PLAN_META[planKey];
  const features = FEATURES[planKey] || [];
  const IconComp = meta.Icon;
  const monthly = plan.price_monthly;
  const yearly = plan.price_yearly;
  const priceLabel = monthly === 0 ? 'Free forever' : '\u20a6' + monthly.toLocaleString() + '/mo';
  const yearlyLabel = yearly > 0 ? '\u20a6' + yearly.toLocaleString() + '/yr' : null;

  return (
    <Card
      className={'border-2 ' + (isCurrent ? meta.border + ' ring-2 ring-offset-2 ' + meta.ringCls : 'border-slate-200')}
      data-testid={'plan-card-' + planKey}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={'p-2.5 rounded-xl bg-gradient-to-br ' + meta.gradient}>
              <IconComp size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 capitalize">{planKey}</h3>
              <p className="text-sm text-slate-500">{priceLabel}</p>
              {yearlyLabel && <p className="text-xs text-slate-400">{yearlyLabel} (save 30%+)</p>}
            </div>
          </div>
          {isCurrent && (
            <Badge className={meta.badgeCls} data-testid={'current-plan-badge-' + planKey}>Current</Badge>
          )}
        </div>

        <div className="space-y-2 mb-4">
          {features.map((f, i) => (
            <FeatureRow key={i} text={f.text} ok={f.ok} />
          ))}
        </div>

        {!isCurrent && planKey !== 'free' && (
          <div className="space-y-2">
            <Button
              onClick={() => onUpgrade(planKey, 'monthly')}
              disabled={upgrading === planKey}
              className={'w-full h-12 rounded-xl bg-gradient-to-r text-white font-bold ' + meta.gradient}
              data-testid={'upgrade-btn-' + planKey}
            >
              {upgrading === planKey ? 'Processing...' : `Upgrade Monthly – ${priceLabel}`}
            </Button>
            {yearly > 0 && (
              <Button
                onClick={() => onUpgrade(planKey, 'yearly')}
                disabled={upgrading === planKey}
                variant="outline"
                className="w-full h-10 rounded-xl text-sm"
                data-testid={'upgrade-yearly-btn-' + planKey}
              >
                Upgrade Yearly – {yearlyLabel}
              </Button>
            )}
          </div>
        )}
        {isCurrent && planKey !== 'free' && expiry && (
          <div className="text-center text-sm text-slate-500 font-medium py-2">
            Active until {new Date(expiry).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [plans, setPlans] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.all([
        axios.get(API_URL + '/api/subscription/plans'),
        axios.get(API_URL + '/api/subscription/me', { headers: getAuthHeaders() }),
      ]);
      setPlans(results[0].data.plans);
      setMySub(results[1].data);
    } catch (e) {
      toast.error('Failed to load plans');
    }
  };

  const handleUpgrade = async (planType, billing) => {
    setUpgrading(planType);
    try {
      await axios.post(
        API_URL + '/api/subscription/upgrade?plan_type=' + planType + '&billing=' + billing,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Upgraded to ' + planType.charAt(0).toUpperCase() + planType.slice(1) + '!');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade failed');
    } finally {
      setUpgrading(null);
    }
  };

  const currentPlan = mySub?.subscription?.plan_type || 'free';
  const expiry = mySub?.subscription?.expiry_date || null;
  const usage = mySub?.usage || {};

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="pricing-screen">
      <div className="app-container px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/profile')}
            className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Plans & Pricing</h1>
            <p className="text-sm text-slate-500">app.aegispay.app</p>
          </div>
        </div>

        {mySub && <UsageCard usage={usage} />}

        <div className="space-y-4">
          {['free', 'premium', 'pro'].map((key) => {
            const plan = plans ? plans[key] : null;
            if (!plan) return null;
            return (
              <PlanCard
                key={key}
                planKey={key}
                plan={plan}
                isCurrent={currentPlan === key}
                expiry={expiry}
                onUpgrade={handleUpgrade}
                upgrading={upgrading}
              />
            );
          })}
        </div>

        <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <p className="text-sm text-slate-600 font-medium">
            Premium members enjoy lower fees and more control over their savings circles.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Payment is simulated for demo. In production, upgrades are processed via secure payment.
          </p>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
